import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { CubeStatus } from 'Cube/domain/cube.domain.types'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import {
  quoteTemplateResponseToCommonFields,
  quoteTemplateResponseToQuoteFields
} from 'Cube/communication/external/quoteTemplates.api.v1/adapters/quoteTemplate.adapters.in'
import {
  pricesToDomainPrices,
  quotePhasesToDiscounts,
  quotePhasesToMinimums,
  quotePhasesToPhases
} from 'Cube/communication/external/common.api.v1/adapters/common.adapters.in'
import { useLoadProducts } from 'Cube/communication/external/common.api.v1/ports/entityLoaders'
import { arrayToIdKeyedObject } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { apiPresentationToQuotePresentation } from '@sequencehq/quote-content'
import { omit } from 'lodash/fp'
import { usePermissions } from 'lib/rbac'
import { AMENDMENT_QUOTE_UNAVAILABLE_REASON } from 'common/menuItems/quotes/quotes.constants'

type QuoteTemplate = DashboardApi20240730.GetQuoteTemplate.Response

type UseApplyQuoteTemplate = () => {
  onSelection: (template: QuoteTemplate) => void
  visible: boolean
  disabled: boolean
  disabledReason?: string
  hasSalesforceOpportunityId: boolean
}

export const useApplyQuoteTemplate: UseApplyQuoteTemplate = () => {
  const ctx = useCubeContext()
  const { can } = usePermissions()

  const priceProductLoader = useLoadProducts()
  const isQuoteADraftOfAPublishedQuote =
    ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible

  const isAmendment = ctx.queries.rawData.data.quote.dealType === 'AMENDMENT'

  return {
    visible:
      can('org.quote:update') &&
      ctx.queries.availableFeatures.quote.edit.available.enabled &&
      ctx.queries.rawData.data.common.status === CubeStatus.QuoteDraft &&
      !isQuoteADraftOfAPublishedQuote,
    disabled: isAmendment,
    disabledReason: isAmendment
      ? AMENDMENT_QUOTE_UNAVAILABLE_REASON
      : undefined,
    hasSalesforceOpportunityId:
      !!ctx.queries.rawData.data.quote.salesforceOpportunityId,
    onSelection: async (quoteTemplate: QuoteTemplate) => {
      const allPrices =
        quoteTemplate.phases
          ?.flatMap(({ prices }) => prices)
          .map(price => ({
            ...price,
            currency: quoteTemplate.template.currency as Currency
          })) ?? []
      const allProductsResult = await priceProductLoader({
        prices: allPrices
      })
      const transformedPhases = quotePhasesToPhases(quoteTemplate.phases)
      const existingPhaseIds = Object.keys(ctx.queries.rawData.data.phases)

      /**
       * This is a trimmed down version of what we do in quoteTemplate.adapters.in.ts.
       * But since this is not a full reset of the editor, we don't need to
       * everything from that.
       */
      ctx.mutators.updateData({
        common: omit([
          'createdAt',
          'customerId',
          'alias',
          'id',
          'isArchived',
          'status'
        ])(
          quoteTemplateResponseToCommonFields(
            quoteTemplate,
            ctx.queries.rawData.configuration.currency.enabled,
            Object.values(transformedPhases)
          )
        ),
        presentation: apiPresentationToQuotePresentation({
          presentation: quoteTemplate.template.presentation
        }),
        phases: transformedPhases,
        quote: omit('salesforceOpportunityId')(
          quoteTemplateResponseToQuoteFields(
            quoteTemplate,
            ctx.queries.rawData.data.quote.createdByEmail
          )
        ),
        products: arrayToIdKeyedObject(allProductsResult.products),
        discounts: quotePhasesToDiscounts(quoteTemplate.phases),
        minimums: quotePhasesToMinimums(quoteTemplate.phases),
        prices: arrayToIdKeyedObject(
          pricesToDomainPrices(
            allPrices,
            quoteTemplate.template.currency as Currency
          )
        )
      })

      /**
       * When applying a quote template to an existing quote, remove any existing phases from before the template
       * was applied
       */
      const existingPhaseIdsToRemove = existingPhaseIds.filter(
        phaseId => !Object.keys(transformedPhases).includes(phaseId)
      )
      ctx.mutators.deleteData({ type: 'phases', ids: existingPhaseIdsToRemove })

      /**
       * We need to give blocknote a kick to load in the new presentation.
       * This will do just that.
       */
      ctx.mutators.updateEditor({
        quoteEditorNeedsReinitialization: true
      })
    }
  }
}
